import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import LearnShell, { useLearnAccess } from "./LearnShell";
import CourseRow from "./CourseRow";
import NotifyForm from "./NotifyForm";
import ContributorModal from "./ContributorModal";
import PackageSheet from "./PackageSheet";
import { fetchCourses, FORMAT_LABEL, countLabel, progressPct, formatPrice, priceFor } from "./learnApi";

const SUMMIT_TOPIC = "Summit 2026";

const PlayIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="currentColor" aria-hidden="true"><path d="M3 1.5v11l9-5.5z"/></svg>
);

const HeroCourse = ({ course }) => {
  if (!course) return null;
  const pct = progressPct(course);
  const resume = pct > 0 && pct < 100 && course.last_position;
  const isGuide = course.format === "guide";
  const noLessons = Number(course.lesson_count) === 0;
  const primaryTo = isGuide || course.locked || noLessons
    ? `/learn/${course.slug}`
    : resume
      ? `/learn/${course.slug}/watch/${course.last_position}`
      : `/learn/${course.slug}/watch/1`;
  const primaryLabel = course.locked
    ? "Unlock the recordings"
    : isGuide
      ? "Open guide"
      : noLessons
        ? "See what's coming"
        : resume
          ? `Continue · Lesson ${course.last_position}`
          : "Start watching";
  const bg = course.hero_image_url || course.cover_image_url;

  return (
    <section className="learn-hero">
      {bg && <div className="learn-hero__bg" style={{ backgroundImage: `url(${bg})` }} aria-hidden="true" />}
      <div className="learn-hero__inner">
        <div className="learn-hero__copy">
          <div className="learn-eyebrow learn-eyebrow--light">
            Postaway Academy · {FORMAT_LABEL[course.format] || "Course"} · {countLabel(course)}
          </div>
          <h1 className="learn-hero__title">{course.title}</h1>
          {course.subtitle && <p className="learn-hero__subtitle">{course.subtitle}</p>}
          {course.instructor_name && (
            <div className="learn-byline">
              {course.instructor_avatar_url && <img src={course.instructor_avatar_url} alt="" />}
              <span>
                With <strong>{course.instructor_name}</strong>
                {course.instructor_title ? ` · ${course.instructor_title}` : ""}
              </span>
            </div>
          )}
          <div className="learn-hero__actions">
            <Link to={primaryTo} className="learn-btn learn-btn--primary">
              {!course.locked && !isGuide && !noLessons && <PlayIcon />}
              {primaryLabel}
            </Link>
            <Link to={`/learn/${course.slug}`} className="learn-btn learn-btn--ghost">Details</Link>
          </div>
        </div>
        <Link to={`/learn/${course.slug}`} className="learn-hero__media" aria-label={`Open ${course.title}`}>
          {bg && <img src={course.cover_image_url || bg} alt="" />}
          <span className="learn-hero__play" aria-hidden="true">
            <PlayIcon size={22} />
          </span>
        </Link>
      </div>
    </section>
  );
};

// Coming-soon hero: no course link-out, notify-me inline.
const AcademyHero = () => (
  <section className="learn-hero learn-hero--soon">
    <div className="learn-hero__bg" style={{ backgroundImage: "url(/summit-hero-aframe.jpeg)" }} aria-hidden="true" />
    <div className="learn-hero__inner">
      <div className="learn-hero__copy">
        <div className="learn-eyebrow learn-eyebrow--light">Postaway Academy · Coming soon</div>
        <h1 className="learn-hero__title">The Hospitality Summit, on demand.</h1>
        <p className="learn-hero__subtitle">
          Every 2026 session, filmed in Breckenridge — plus courses, guides and masterclasses from the hosts and
          creators who do this every day. Summit attendees get a special rate; everyone else can pick up a package.
        </p>
        <NotifyForm compact source="academy_hero" className="learn-hero__notify" />
        <p className="learn-hero__fine">Recordings land within a month of the summit. No spam — one email when it's live.</p>
      </div>
      <div className="learn-hero__media learn-hero__media--static" aria-hidden="true">
        <img src="/summit-hero-aframe.jpeg" alt="" />
        <span className="learn-hero__soon">Coming soon</span>
      </div>
    </div>
  </section>
);

const ContributorBand = ({ onApply }) => (
  <section className="learn-contrib">
    <div className="learn-contrib__copy">
      <div className="learn-eyebrow learn-eyebrow--light">Become a contributor</div>
      <h2 className="learn-contrib__title">Teach what you know. Get paid while you sleep.</h2>
      <p>
        Postaway Academy is built by the community. If you've got a masterclass, a webinar, or a topic people
        keep asking you about, we'll host it, sell it and share the revenue with you.
      </p>
      <ul className="learn-contrib__points">
        <li><strong>Reach</strong> thousands of hosts and creators already on Postaway</li>
        <li><strong>Earn</strong> a share every time your content is watched</li>
        <li><strong>Skip the setup</strong> — we handle hosting, streaming and payments</li>
      </ul>
    </div>
    <div className="learn-contrib__cta">
      <button type="button" className="learn-btn learn-btn--primary" onClick={onApply}>Apply to contribute</button>
      <span className="learn-contrib__fine">Applications open now · spots roll out with the Academy launch</span>
    </div>
  </section>
);

// Body is a child of LearnShell so it can read the access context.
const HomeBody = () => {
  const access = useLearnAccess();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [contribOpen, setContribOpen] = useState(false);
  const [sheet, setSheet] = useState(null); // null | 'buy' | 'claim'

  useEffect(() => {
    let cancelled = false;
    fetchCourses()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
    // Refetch when access changes (purchase / claim) so locks update.
  }, [access.entitlements.length, access.mode]);

  const courses = data?.courses || null;
  const topics = useMemo(() => data?.topics || [], [data]);
  const packages = data?.packages || access.packages || [];
  const comingSoon = access.mode === "coming_soon";

  const rows = useMemo(() => {
    if (!courses) return null;
    const summit = courses.filter((c) => c.access === "package");
    const hero = courses.find((c) => c.is_featured) || courses[0] || null;
    const continueWatching = courses
      .filter((c) => Number(c.seconds_watched) > 0 && progressPct(c) < 100)
      .sort((a, b) => new Date(b.last_watched_at || 0) - new Date(a.last_watched_at || 0));
    const saved = courses.filter((c) => c.is_saved);
    const fresh = courses.filter((c) => c.is_new && c.format !== "guide" && c.access !== "package");
    const guides = courses.filter((c) => c.format === "guide");
    const byTopic = topics
      .filter((t) => t !== "Getting Started" && t !== SUMMIT_TOPIC)
      .map((t) => ({ topic: t, items: courses.filter((c) => c.topic === t && c.format !== "guide") }))
      .filter((g) => g.items.length > 0);
    return { hero, summit, continueWatching, saved, fresh, guides, byTopic };
  }, [courses, topics]);

  const summitPackages = packages.filter((p) => p.slug.startsWith("summit-2026"));
  const summitLocked = rows?.summit?.some((c) => c.locked);
  const summitUnlocked = rows?.summit?.length > 0 && rows.summit.every((c) => !c.locked);
  const isAttendee = Boolean(access.isAttendee);
  const cheapest = summitPackages.slice().sort((a, b) => priceFor(a, isAttendee) - priceFor(b, isAttendee))[0];
  const cheapestAttendee = summitPackages.slice().sort((a, b) => priceFor(a, true) - priceFor(b, true))[0];

  const summitSubtitle = comingSoon
    ? `Recordings arrive within a month of the summit. Attendees from ${cheapestAttendee ? formatPrice(priceFor(cheapestAttendee, true)) : "$100"} per track; everyone else from ${cheapest ? formatPrice(cheapest.price_cents) : "$499"}. Speakers stream free.`
    : summitUnlocked
      ? "You have access. New sessions are added as they're edited."
      : isAttendee
        ? `You were there — your attendee rate starts at ${cheapest ? formatPrice(priceFor(cheapest, true)) : "$100"} per track.`
        : `Packages from ${cheapest ? formatPrice(cheapest.price_cents) : "$499"}. Attended the summit? Your rate is ${cheapestAttendee ? formatPrice(priceFor(cheapestAttendee, true)) : "$100"} per track with the email you registered with.`;

  return (
    <>
      {error && <div className="learn-error">We couldn't load the library right now. Please refresh.</div>}
      {rows && (
        <>
          {comingSoon ? <AcademyHero /> : <HeroCourse course={rows.hero} />}

          <div className="learn-container">
            <nav className="learn-chips" aria-label="Browse by topic">
              {comingSoon ? (
                <>
                  <span className="learn-chip learn-chip--active learn-chip--static">All</span>
                  {topics.map((t) => <span key={t} className="learn-chip learn-chip--static">{t}</span>)}
                  <span className="learn-chip learn-chip--static">Guides</span>
                </>
              ) : (
                <>
                  <Link to="/learn/library" className="learn-chip learn-chip--active">All</Link>
                  {topics.map((t) => (
                    <Link key={t} to={`/learn/library?topic=${encodeURIComponent(t)}`} className="learn-chip">{t}</Link>
                  ))}
                  <Link to="/learn/library?format=guide" className="learn-chip">Guides</Link>
                </>
              )}
            </nav>

            {rows.summit.length > 0 && (
              <div className="learn-summit">
                <CourseRow
                  id="summit"
                  title="Hospitality Summit 2026"
                  subtitle={summitSubtitle}
                  courses={rows.summit}
                  disabled={comingSoon}
                  packages={summitPackages}
                  isAttendee={isAttendee}
                />
                {!comingSoon && summitLocked && (
                  <div className="learn-summit__actions">
                    <button type="button" className="learn-btn learn-btn--primary" onClick={() => setSheet("buy")}>
                      Unlock the recordings
                    </button>
                    <button type="button" className="learn-btn learn-btn--ghost" onClick={() => setSheet("claim")}>
                      I'm a 2026 speaker — claim free access
                    </button>
                  </div>
                )}
                {comingSoon && (
                  <div className="learn-summit__packages">
                    {summitPackages.map((p) => (
                      <div key={p.slug} className="learn-summit__package">
                        <span className="learn-summit__package-title">{p.title.replace(/^Summit 2026\s*/, "")}</span>
                        {p.subtitle && <span className="learn-summit__package-sub">{p.subtitle}</span>}
                        <span className="learn-summit__package-price">{formatPrice(p.price_cents)}</span>
                        {p.attendee_price_cents != null && (
                          <span className="learn-summit__package-attendee">
                            2026 attendees: <strong>{formatPrice(p.attendee_price_cents)}</strong>
                          </span>
                        )}
                      </div>
                    ))}
                    <div className="learn-summit__package learn-summit__package--free">
                      <span className="learn-summit__package-title">2026 speakers</span>
                      <span className="learn-summit__package-sub">Thank you — every session is on us. Watch for an email from Jamie.</span>
                      <span className="learn-summit__package-price">Free</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!comingSoon && (
              <CourseRow
                title="Continue watching"
                subtitle="Pick up where you left off."
                courses={rows.continueWatching}
              />
            )}
            {!comingSoon && (
              <CourseRow
                title="New this month"
                subtitle="Fresh additions to the hub."
                courses={rows.fresh}
                seeAllTo="/learn/library"
              />
            )}
            {rows.byTopic.map((g) => (
              <CourseRow
                key={g.topic}
                title={g.topic}
                courses={g.items}
                seeAllTo={`/learn/library?topic=${encodeURIComponent(g.topic)}`}
                disabled={comingSoon}
                packages={packages}
                isAttendee={isAttendee}
              />
            ))}
            <CourseRow
              title="Guides & templates"
              subtitle="Downloadable PDFs you can hand to your team."
              courses={rows.guides}
              seeAllTo="/learn/library?format=guide"
              disabled={comingSoon}
            />
            {!comingSoon && (
              <CourseRow
                title="My list"
                subtitle="Courses you've saved."
                courses={rows.saved}
              />
            )}

            <ContributorBand onApply={() => setContribOpen(true)} />

            <div className="learn-cta-band">
              <div>
                <h2 className="learn-cta-band__title">Postaway Inner Circle: From Posting to Profit</h2>
                <p>
                  A live, guided social media program for hosts and creators. 12 sessions on Google Meet and 12 weekly
                  challenges, January 20 – April 21, 2027.
                </p>
              </div>
              <Link to="/inner-circle" className="learn-btn learn-btn--primary">Explore Inner Circle</Link>
            </div>

            {comingSoon ? (
              <div className="learn-cta-band learn-cta-band--notify">
                <div>
                  <h2 className="learn-cta-band__title">Be first in when the doors open.</h2>
                  <p>One email when the summit recordings are live. Tell us who you are so we send you the right stuff.</p>
                </div>
                <NotifyForm source="academy_footer" className="learn-cta-band__form" />
              </div>
            ) : (
              <div className="learn-cta-band">
                <div>
                  <h2 className="learn-cta-band__title">Looking for something specific?</h2>
                  <p>Filter the full library by topic, format and level.</p>
                </div>
                <Link to="/learn/library" className="learn-btn learn-btn--primary">Browse the library</Link>
              </div>
            )}
          </div>

          <ContributorModal open={contribOpen} onClose={() => setContribOpen(false)} />
          <PackageSheet
            open={sheet !== null}
            initialTab={sheet === "claim" ? "claim" : "buy"}
            onClose={() => setSheet(null)}
            packages={summitPackages}
            isAttendee={isAttendee}
            returnTo="/learn"
          />
        </>
      )}
    </>
  );
};

const LearnHome = () => (
  <LearnShell dark>
    <HomeBody />
  </LearnShell>
);

export default LearnHome;
